import type { SubmitAction, ActionResult } from '../types/actions';
import { isElementVisible, isElementEnabled, scrollIntoView } from '../utils/dom-utils';
import { waitForElement, waitForNavigation } from '../utils/wait-utils';

export async function executeSubmitAction(action: SubmitAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find the form element
    let element = document.querySelector<HTMLFormElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLFormElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Form not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Verify it's a form element
    if (!(element instanceof HTMLFormElement)) {
      return {
        success: false,
        action,
        error: 'Element is not a form',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if visible and enabled
    if (!isElementVisible(element)) {
      return {
        success: false,
        action,
        error: 'Form is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    if (!isElementEnabled(element)) {
      return {
        success: false,
        action,
        error: 'Form is disabled',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if needed
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
    }

    // Track if navigation might occur
    const currentUrl = window.location.href;

    // Submit the form
    element.submit();

    // Wait for navigation if specified
    if (action.waitForNavigation) {
      await waitForNavigation(currentUrl);
    }

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
